import { useCallback, useState } from 'react'
import moment from 'moment'
import {
  isFlightVerifiedForBooking,
  isValidFlightNumberFormat,
} from 'components/FlightTrackingNew/flightNervePure'
import {
  applyBuffer,
  isChauffeurArrivalBuffer,
} from 'helpers/chauffeurArrivalBuffer'
import { departureLookupDate } from './PartnerFlightInfo'

// A lookup result only counts for the flight/date it was fetched for. Keyed rather than
// reset in an effect: a cached verdict is reported from a child effect in the same commit
// the booking loads, and a parent reset effect would run after it and wipe it.
const useKeyedResult = (key, accept) => {
  const [result, setResult] = useState({ key: null, data: undefined })
  const onResult = useCallback(
    data => setResult({ key, data: accept(data) ? data : null }),
    [key]
  )
  return [result.key === key ? result.data : undefined, onResult]
}

const hasArrival = data => Boolean(data?.time?.arrival)
const hasDepartureOrArrival = data =>
  Boolean(data?.time?.departure || data?.time?.arrival)

// The shared rule the client and admin booking pages use (flightNervePure): the looked-up
// flight is at the booked airport and on the booked day. Without it a lookup that resolved
// another day's flight (5629-V, AC866) showed its card and drove the buffered pick-up time.
const isVerifiedForLeg = (data, side, location, bookedTime) =>
  Boolean(data) &&
  isFlightVerifiedForBooking({
    airportInfo:
      side === 'departure' ? data.data?.departureInfo : data.data?.arrivalInfo,
    flightTime:
      side === 'departure' ? data.time?.departure : data.time?.arrival,
    side,
    bookedAirport: { iata: location?.iata, fullAddress: location?.fullAddress },
    bookedTime,
  })

// Partner booking details (RD-4074): arrival-flight verification drives Date and time,
// the blue buffer banner, and whether the expanded FlightTracking card is shown.
export default function usePartnerFlightDisplay(list) {
  const flightNumber = list?.airportPickUpInfo?.flightNumber || null
  const buffer = list?.airportPickUpInfo?.chauffeurArrivalBuffer
  // RD-4144: an admin set this pick-up by hand; it is shown as stored, never as landing + buffer.
  const manual = list?.airportPickUpInfo?.chauffeurArrivalManual === true
  const formatValid = Boolean(
    flightNumber && isValidFlightNumberFormat(flightNumber)
  )

  // undefined = lookup not settled; null = valid format but not trackable; object = found,
  // verified only when it is at the booked airport on the booked day
  const [flightData, onFetchFlightData] = useKeyedResult(
    `${flightNumber}|${list?.pickUpDate}`,
    hasArrival
  )

  const verified =
    formatValid &&
    hasArrival(flightData) &&
    isVerifiedForLeg(
      flightData,
      'arrival',
      list?.pickUpLocation,
      list?.pickUpDate
    )
  const unverified = formatValid && flightData !== undefined && !verified
  const hasBuffer = isChauffeurArrivalBuffer(buffer)

  // Banner body (Figma): tracked landing time, not pickup = landing + buffer.
  const scheduledFlightArrival =
    verified && hasBuffer ? applyBuffer(flightData.time.arrival, 0) : null

  // The booking's own pick-up, the time admin, client and the offers list show. It used to be
  // recomputed here as scheduled landing + buffer, which disagrees with it whenever admin set the
  // time from the ESTIMATED landing (RD-4144 "Adjust buffer") or by hand.
  const storedPickUp = list?.pickUpDate ? moment.utc(list.pickUpDate) : null
  const displayPickUp =
    storedPickUp && storedPickUp.isValid() ? storedPickUp : null

  // Verified + buffer → Figma buffer banner; valid-but-untracked → track-manually banner;
  // invalid format → no blue banner.
  const bannerVariant =
    verified && hasBuffer ? 'verified' : unverified ? 'unverified' : null

  const depFlightNumber = list?.airportDropOffInfo?.flightNumber || null
  const depFormatValid = Boolean(
    depFlightNumber && isValidFlightNumberFormat(depFlightNumber)
  )
  const [depFlightData, onFetchDepFlightData] = useKeyedResult(
    `${depFlightNumber}|${list?.dropOffDate}|${list?.pickUpDate}`,
    hasDepartureOrArrival
  )
  const depVerified =
    depFormatValid &&
    hasDepartureOrArrival(depFlightData) &&
    isVerifiedForLeg(
      depFlightData,
      'departure',
      list?.dropOffLocation,
      list ? departureLookupDate(list) : null
    )

  return {
    verified,
    buffer,
    manual,
    scheduledFlightArrival,
    displayPickUp,
    bannerVariant,
    onFetchFlightData,
    shouldMountArrivalTracking: formatValid,
    depVerified,
    onFetchDepFlightData,
    shouldMountDepartureTracking: depFormatValid,
  }
}
